import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";

import { BaseComponent, NotificationService, UserService, avatarColorFor, initialsFor } from "@mypxplat/xplat/core";
import { AppService, AvoService, WebCommunityService } from "@mypxplat/xplat/web/core";

// Deep import, not the `../modals` barrel — the barrel is not re-exported for this one, and
// `profile.component.ts` reaches for it the same way.
import { AddConnectionComponent } from "../modals/add-connection/add-connection.component";

/** Where the card sits relative to its anchor, once the viewport has had its say. */
type VerticalPlacement = "below" | "above";

/** Gap between the avatar and the card, in px. Doubles as the invisible hover bridge height. */
const ANCHOR_GAP = 8;
/** Keep the card this far from the viewport edges when clamping. */
const VIEWPORT_MARGIN = 12;
/** Must match `$card-width` in the scss — the clamp math needs it before the card has painted. */
const CARD_WIDTH = 300;

/**
 * Slack-style hover card for a community member (DENG-846).
 *
 * Never placed in a template directly — `UserHoverCardDirective` creates exactly one of
 * these at a time and parents it to `document.body`, so the card can escape the
 * `overflow: hidden` and stacking contexts of the post it was summoned from. The
 * directive owns the open/close timers; this component owns its own content, its own
 * placement, and the two actions on it.
 *
 * Data comes from `getUserProfile` (the Firestore `user_profiles` doc) rather than the
 * `exchange/creator` endpoint the profile PAGE uses, for two reasons: it is the only one
 * of the two that carries `tagline`, and it is already memory-cached by community service
 * for every author in the feed, so a second hover over the same person costs nothing.
 *
 * Deliberately `aria-hidden`: it is a pointer-only shortcut, and everything on it is
 * reachable without it — the avatar underneath is a link to the same profile page, and
 * that page carries the same Connect action. Exposing a duplicate set of controls that
 * only appear on hover would be worse for a screen reader than omitting them.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-user-hover-card",
  templateUrl: "user-hover-card.component.html",
  styleUrls: ["user-hover-card.component.scss"],
  standalone: false,
})
export class UserHoverCardComponent extends BaseComponent implements OnDestroy {
  /** The element the card is anchored to — the avatar being hovered. */
  @Input() anchor: HTMLElement;

  /** Pointer entered the card itself; the directive uses this to cancel its close timer. */
  @Output() keepOpen: EventEmitter<void> = new EventEmitter();
  /** Pointer left the card, or an action closed it. */
  @Output() requestClose: EventEmitter<void> = new EventEmitter();

  public loading = true;
  /** Set when the Firestore profile doc is missing — imported/ghost authors have no doc. */
  public unavailable = false;
  /** Their profile is not public and they are not the signed-in user: name only, no actions. */
  public isPrivate = false;
  public isMe = false;

  public name = "";
  public photo = "";
  public coverPhoto = "";
  public tagline = "";
  public initials = "?";
  public avatarColor = "";

  /**
   * Connection state drives which action shows. Mirrors the profile page's tri-state, read
   * from `connectionMap` (which `updateConnections` keys by the OTHER person's id).
   */
  public connectionState: "none" | "sent" | "received" | "connected" = "none";
  public connecting = false;

  public placement: VerticalPlacement = "below";

  private _userId: string;
  private _destroyed = false;

  @Input() set userId(value: string) {
    if (!value || value === this._userId) return;
    this._userId = value;
    this._load();
  }
  get userId(): string {
    return this._userId;
  }

  constructor(
    userService: UserService,
    private _el: ElementRef<HTMLElement>,
    private _cdr: ChangeDetectorRef,
    private _router: Router,
    private _appService: AppService,
    private _avoService: AvoService,
    private _notificationService: NotificationService,
    public communityService: WebCommunityService
  ) {
    super(userService);
  }

  ngOnDestroy() {
    this._destroyed = true;
    super.ngOnDestroy();
  }

  /**
   * Position against the anchor. Called once on open and again after the profile resolves,
   * because the card's height changes when the cover photo and tagline arrive and an
   * "above" placement is measured from the bottom edge.
   */
  reposition() {
    if (!this.anchor || this._destroyed) return;
    const host = this._el.nativeElement;
    const anchorRect = this._anchorRect();
    const cardRect = host.getBoundingClientRect();
    const cardHeight = cardRect.height || 0;

    const roomBelow = window.innerHeight - anchorRect.bottom;
    const roomAbove = anchorRect.top;
    // Prefer below (reading direction); flip only when below genuinely doesn't fit AND
    // above fits better, so a card near the bottom of a short viewport doesn't thrash.
    this.placement = roomBelow < cardHeight + ANCHOR_GAP + VIEWPORT_MARGIN && roomAbove > roomBelow ? "above" : "below";

    const top = this.placement === "above" ? Math.max(VIEWPORT_MARGIN, anchorRect.top - cardHeight - ANCHOR_GAP) : anchorRect.bottom + ANCHOR_GAP;

    // Left-align with the avatar, then pull back inside the viewport. `CARD_WIDTH` rather
    // than the measured width: on the first pass the card may not have painted yet.
    const width = cardRect.width || CARD_WIDTH;
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(anchorRect.left, maxLeft));

    host.style.top = `${Math.round(top)}px`;
    host.style.left = `${Math.round(left)}px`;
  }

  /**
   * The box to anchor against — usually the host element's own, but not always.
   *
   * The community post avatar is an `<a class="profile-photo-container">` that is
   * `display: inline` with NO box of its own; the 50px photo inside it is
   * `position: absolute` at a negative offset, so it paints well outside the link's
   * origin. Hovering still works (mouseenter fires on ancestors of the entered element),
   * but the link's own rect is 0×0 and tens of pixels away from the photo the user is
   * actually pointing at. So: when the host has no area, anchor to the biggest box its
   * children DO paint — the photo. Biggest rather than the union of them all, because
   * these links also carry a 1×1 `.sr-only` span parked off to one side, and unioning
   * that in would drag the card away from the avatar again.
   */
  private _anchorRect(): DOMRect {
    const own = this.anchor.getBoundingClientRect();
    if (own.width > 0 && own.height > 0) return own;

    let best: DOMRect | null = null;
    Array.from(this.anchor.children).forEach((child) => {
      const rect = child.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      if (!best || rect.width * rect.height > best.width * best.height) best = rect;
    });

    return best || own;
  }

  onMouseEnter() {
    this.keepOpen.emit();
  }

  onMouseLeave() {
    this.requestClose.emit();
  }

  viewProfile() {
    this.requestClose.emit();
    this._router.navigate(["/profile", this._userId]);
  }

  /**
   * Open the thread with this member. Only offered once the connection is accepted —
   * `/messages/:id` is behind the auth guard and a thread with someone who hasn't accepted
   * you isn't a thing. Same navigation the profile page's message icon performs.
   */
  sendMessage() {
    this.requestClose.emit();
    this._router.navigate(["/messages", this._userId]);
  }

  /**
   * Opens the same message-first modal the profile page uses, then runs the same follow-up:
   * send the invite, email it, track it. The twin lives at `profile.component.ts#addConnection`
   * — if the flow changes, both have to change. They are not shared yet because the profile
   * page's copy also refreshes its own connections/invites lists off the response.
   */
  connect() {
    if (this.connecting || !this.user) return;

    // NO subscription gate. Connecting is person-to-person, not participation in a
    // community, so it is not the place to apply Fender Studio Pro's membership rule — a
    // Tone Master Pro member with no FSP subscription was being bounced to FSP onboarding
    // for trying to connect with someone. Same class of bug as the reaction gate in
    // `comment.component.updateReactionGate`. paeapi's `POST /community/connection` asks
    // only for authentication, so there is nothing server-side to mirror here either.

    const modalRef = this._appService.showModal(AddConnectionComponent, { size: "md", ariaLabelledBy: "modal-title" });
    modalRef.componentInstance.title = `Connect with ${this.name}`;
    modalRef.componentInstance.requestConnection.subscribe((message: string) => {
      this._sendConnectionRequest(message);
    });
    // The modal owns the interaction from here; the card has served its purpose.
    this.requestClose.emit();
  }

  private _sendConnectionRequest(message: string = "") {
    this.connecting = true;
    this.communityService.addConnection(this._userId, message).subscribe(
      (connections: any[]) => {
        this.connecting = false;
        const connection = (connections || []).filter((item) => item.requested_user_id == this._userId || item.initiated_by == this._userId)[0];
        if (connection) this._sendConnectionInviteEmail(connection);
        this._resolveConnectionState();
        this._trackConnectionSent();
        this._cdr.markForCheck();
      },
      () => {
        this.connecting = false;
        this._appService.alert("Sorry, we couldn't send that connection request. Please try again.");
        this._cdr.markForCheck();
      }
    );
  }

  private _sendConnectionInviteEmail(connection: any) {
    const notification = this._notificationService.createCommunityConnectionInvite(this.userService.user, connection, connection.message || "");
    this._notificationService
      .sendNotification({
        notification,
        user_ids: [connection.requested_user_id],
        clients: ["rollup", "mobile", "email"],
      })
      .subscribe(
        () => {},
        () => {}
      );
  }

  private _trackConnectionSent() {
    const te = this._avoService.trackEvent();
    if (this.userService.appId === "fender" && te.communityViewed) {
      const page = this._avoService.getPageProperties({ pageName: "Community Connection Sent" });
      const extra = {
        name: "Community Connection Sent",
        // Distinguishes hover-card connects from the profile page's, which sends "profile".
        channelName: "hover_card",
        eventType: "connection",
        interactionType: "sent",
      };
      te.communityViewed(this._avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true }));
    } else if (te.communityConnectionSent) {
      te.communityConnectionSent({ siteId: "my_fender" });
    }
  }

  private _load() {
    this.loading = true;
    this.unavailable = false;
    this.isMe = !!this.user && String(this.user.id) === String(this._userId);

    this.communityService
      .getUserProfile(this._userId)
      .then((profile: any) => {
        if (this._destroyed) return;
        this.loading = false;
        if (!profile) {
          this.unavailable = true;
          this._renderThenReposition();
          return;
        }
        this._applyProfile(profile);
        this._resolveConnectionState();
        this._renderThenReposition();
      })
      .catch(() => {
        if (this._destroyed) return;
        this.loading = false;
        this.unavailable = true;
        this._renderThenReposition();
      });
  }

  /**
   * Flush the new content to the DOM, THEN measure.
   *
   * `markForCheck()` only schedules; the card still has its loading height at the moment
   * the call returns. Measuring there put an `above`-placed card ~65px too low — far
   * enough to sit on top of the very avatar that summoned it. `detectChanges()` renders
   * synchronously, so `reposition()` sees the height it is about to paint at.
   */
  private _renderThenReposition() {
    if (this._destroyed) return;
    this._cdr.detectChanges();
    this.reposition();
  }

  private _applyProfile(profile: any) {
    this.name = [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim();
    // `public` is the community-visibility flag — the same one the member search filters on.
    // Their own card always renders in full; the flag is about what OTHERS may see.
    this.isPrivate = profile.public === false && !this.isMe;
    this.initials = initialsFor(this.name);
    this.avatarColor = avatarColorFor(this._userId);
    this.photo = this.isPrivate ? "" : profile.photo || "";
    this.coverPhoto = this.isPrivate ? "" : profile.cover_photo || "";
    this.tagline = this.isPrivate ? "" : profile.tagline || "";
  }

  private _resolveConnectionState() {
    this.connectionState = "none";
    if (this.isMe || !this.user) return;
    const connection = this.communityService.connectionMap?.[this._userId];
    if (!connection) return;
    if (connection.status === "accepted") this.connectionState = "connected";
    else if (connection.status === "pending") this.connectionState = connection.initiated_by == this.user.id ? "sent" : "received";
  }
}
